import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getPublishJobs } from "../../../services/jobService";
import { Modal, Button, Form, Toast, ToastContainer } from "react-bootstrap";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./ViewCreatedJobs.css";

const ViewPostedJobs = () => {
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [jobsPerPage, setJobsPerPage] = useState(10);
	const [showJobTitleFilter, setShowJobTitleFilter] = useState(false);
	const [filteredTitles, setFilteredTitles] = useState([]);
	const [selectedTitles, setSelectedTitles] = useState([]);
	const [searchTitleText, setSearchTitleText] = useState("");
	const [toast, setToast] = useState({
		show: false,
		message: "",
		bg: "success",
	});

	const navigate = useNavigate();
	const totalRecords = jobs.length;

	// Fetch published jobs
	useEffect(() => {
		const fetchJobs = async () => {
			try {
				const data = await getPublishJobs();
				const publishedJobs = data?.data?.publishedJobs;
				if (Array.isArray(publishedJobs)) {
					setJobs(publishedJobs);
				} else {
					throw new Error("Unexpected data format: publishedJobs not found");
				}
			} catch (err) {
				console.error("Error fetching published jobs:", err);
				setError(
					err?.response?.data?.message ||
						err.message ||
						"Failed to fetch published jobs"
				);
			} finally {
				setLoading(false);
			}
		};

		fetchJobs();
	}, []);

	const handleViewDetails = (jobUID) => {
		navigate(`/Dashboard/View-Job-Details/${jobUID}`);
	};

	const handleSubmitCandidate = (jobUID) => {
		navigate(`/Dashboard/send-candidate/${jobUID}`);
	};

	const jobTitles = [...new Set(jobs.map((c) => c.jobTitle))];

	const handleFilterIconClick = () => {
		setShowJobTitleFilter(true);
		setFilteredTitles(jobTitles);
		setSelectedTitles(jobTitles);
		setSearchTitleText("");
	};

	const handleSearchTitleChange = (e) => {
		const value = e.target.value.toLowerCase();
		setSearchTitleText(value);
		setFilteredTitles(
			jobTitles.filter((title) => title.toLowerCase().includes(value))
		);
	};

	const handleSelectTitle = (title) => {
		if (selectedTitles.includes(title)) {
			setSelectedTitles(selectedTitles.filter((t) => t !== title));
		} else {
			setSelectedTitles([...selectedTitles, title]);
		}
	};

	const handleSelectAllTitles = (checked) => {
		setSelectedTitles(checked ? jobTitles : []);
	};

	// Filtered jobs logic
	const filteredJobs = jobs.filter(
		(job) =>
			(selectedTitles.length === 0 || selectedTitles.includes(job.jobTitle)) &&
			[job.jobTitle, job.department, job.location, job.grade, job.function]
				.join(" ")
				.toLowerCase()
				.includes(searchTerm.toLowerCase())
	);

	const indexOfLastJob = currentPage * jobsPerPage;
	const indexOfFirstJob = indexOfLastJob - jobsPerPage;
	const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
	const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
	const hasJobs = jobs.length > 0;

	return (
		<div className="jobs-container container mt-5 pt-5">
			{/* Toast Notifications */}
			<ToastContainer position="top-end" className="p-3">
				<Toast
					bg={toast.bg}
					onClose={() => setToast({ ...toast, show: false })}
					show={toast.show}
					delay={3000}
					autohide
				>
					<Toast.Body className="text-white fw-semibold">
						{toast.message}
					</Toast.Body>
				</Toast>
			</ToastContainer>

			<div className="jobs-card shadow-sm">
				<div className="jobs-header d-flex justify-content-between align-items-center">
					<h3 className="fw-bold mb-0">Live Jobs</h3>
				</div>

				{/* Search + Create Button */}
				<div className="jobs-toolbar d-flex flex-wrap align-items-center gap-3 my-3 w-100 justify-content-between">
					<div
						className="input-group rounded-pill border bg-white shadow-sm"
						style={{ maxWidth: "350px" }}
					>
						<span className="input-group-text bg-transparent border-0 ps-3">
							<i className="bi bi-search text-secondary"></i>
						</span>
						<input
							type="text"
							className="form-control border-0 rounded-5 shadow-none"
							placeholder="Search by title, department, location, etc"
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value);
								setCurrentPage(1);
							}}
						/>
					</div>
					<Button
						variant="primary"
						className="fw-semibold rounded-pill px-4"
						onClick={() => navigate("/Dashboard/PostJob")}
					>
						<i className="bi bi-plus-circle me-2"></i> Create Job
					</Button>
				</div>

				{/* Table or Skeleton */}
				<div className="Overflow-Auto">
					{loading ? (
						<SkeletonTheme baseColor="#e0e0e0" highlightColor="#f5f5f5">
							<table className="table modern-table align-middle">
								<thead>
									<tr>
										<th>#</th>
										<th>Title</th>
										<th>Department</th>
										<th>Location</th>
										<th>Grade</th>
										<th>Candidates</th>
										<th>Function</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{[...Array(5)].map((_, idx) => (
										<tr key={idx}>
											{Array(8)
												.fill()
												.map((__, i) => (
													<td key={i}>
														<Skeleton height={20} />
													</td>
												))}
										</tr>
									))}
								</tbody>
							</table>
						</SkeletonTheme>
					) : (
						<table className="table modern-table align-middle">
							<thead>
								<tr>
									<th>#</th>
									<th>
										Title
										<i
											className="bi bi-funnel ms-2 text-primary"
											onClick={handleFilterIconClick}
											style={{ cursor: "pointer" }}
										></i>
									</th>
									<th>Department</th>
									<th>Location</th>
									<th>Grade</th>
									<th>Candidates</th>
									<th>Function</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody>
								{currentJobs.length > 0 ? (
									currentJobs.map((job, idx) => (
										<tr key={job.jobUID || idx}>
											<td>{indexOfFirstJob + idx + 1}</td>
											<td>{job.jobTitle || "Untitled Job"}</td>
											<td>{job.department || "N/A"}</td>
											<td>{job.location || "N/A"}</td>
											<td>{job.grade || "N/A"}</td>
											<td>
												<Link
													to={`/Dashboard/Sent-Candidates-For-Job/${job.jobUID}`}
												>
													View ({job.candidateReceived || "0"})
												</Link>
											</td>
											<td>{job.function || "N/A"}</td>
											<td>
												<div className="dropdown d-inline mx-3 position-relative">
													<i
														className="bi bi-three-dots-vertical"
														type="button"
														data-bs-toggle="dropdown"
														aria-expanded="false"
													></i>
													<ul className="dropdown-menu shadow-sm border-0">
														<li>
															<button
																className="dropdown-item"
																onClick={() => handleViewDetails(job.jobUID)}
															>
																<i className="bi bi-eye me-2"></i> View Details
															</button>
														</li>
														<li>
															<button
																className="dropdown-item"
																onClick={() =>
																	handleSubmitCandidate(job.jobUID)
																}
															>
																<i className="bi bi-person-plus-fill me-2"></i>
																Submit Candidate
															</button>
														</li>
													</ul>
												</div>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan="8" className="text-center text-danger py-3">
											{hasJobs
												? "No Live Job found."
												: "No new Live Job received."}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					)}
				</div>

				{/* Pagination */}
				{!loading && totalRecords > 10 && (
					<div className="d-flex justify-content-end align-items-center mt-3 gap-3">
						<div className="d-flex align-items-center">
							<label className="me-2 fw-semibold">Items Per Page:</label>
							<Form.Select
								size="sm"
								value={jobsPerPage}
								onChange={(e) => {
									setJobsPerPage(parseInt(e.target.value));
									setCurrentPage(1);
								}}
								className="rounded-pill shadow-sm"
								style={{ width: "100px" }}
							>
								<option value={5}>5</option>
								<option value={10}>10</option>
								<option value={25}>25</option>
								<option value={50}>50</option>
							</Form.Select>
						</div>

						<nav>
							<ul className="pagination pagination-sm mb-0">
								<li
									className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
								>
									<button
										className="page-link"
										onClick={() => setCurrentPage((p) => p - 1)}
									>
										<i className="bi bi-chevron-left"></i>
									</button>
								</li>
								{[...Array(totalPages)].map((_, idx) => (
									<li
										key={idx}
										className={`page-item ${
											currentPage === idx + 1 ? "active" : ""
										}`}
									>
										<button
											className="page-link"
											onClick={() => setCurrentPage(idx + 1)}
										>
											{idx + 1}
										</button>
									</li>
								))}
								<li
									className={`page-item ${
										currentPage === totalPages ? "disabled" : ""
									}`}
								>
									<button
										className="page-link"
										onClick={() => setCurrentPage((p) => p + 1)}
									>
										<i className="bi bi-chevron-right"></i>
									</button>
								</li>
							</ul>
						</nav>
					</div>
				)}
			</div>

			{/* Job Title Filter Modal */}
			<Modal
				show={showJobTitleFilter}
				onHide={() => setShowJobTitleFilter(false)}
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Filter by Job Title</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ maxHeight: "300px", overflowY: "auto" }}>
					<Form.Control
						type="text"
						placeholder="Search Job Titles"
						value={searchTitleText}
						onChange={handleSearchTitleChange}
						className="mb-2"
					/>
					<div className="form-check">
						<input
							className="form-check-input"
							type="checkbox"
							checked={selectedTitles.length === jobTitles.length}
							onChange={(e) => handleSelectAllTitles(e.target.checked)}
						/>
						<label className="form-check-label">Select All</label>
					</div>
					{filteredTitles.map((title, idx) => (
						<div className="form-check" key={idx}>
							<input
								className="form-check-input"
								type="checkbox"
								checked={selectedTitles.includes(title)}
								onChange={() => handleSelectTitle(title)}
								id={`job-title-${idx}`}
							/>
							<label className="form-check-label" htmlFor={`job-title-${idx}`}>
								{title}
							</label>
						</div>
					))}
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => setShowJobTitleFilter(false)}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={() => setShowJobTitleFilter(false)}
					>
						Apply
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default ViewPostedJobs;
